import { RemoteJobTable } from "@/features/remote_jobs";
import { store, StoreState } from "@/store";
import { LargeSiteView, PortfolioView, supabaseApi, useGetCurrentUserQuery, useGetDistinctPortfolioUsersQuery, useGetGroupsQuery, useGetPortfoliosQuery, useGetToolUsageQuery, useGetUsersQuery } from "@/store/services/supabase";
import { portfolioExportActions } from "@/store/slices/portfolioExport";
import { PortfolioExportProgressTracker } from "@/tools/aggregate/portfolio-export/classes/PortfolioExportProgressTracker";
import { PortfolioExportMaxSize } from "@/tools/aggregate/portfolio-export/components/PortfolioExportMaxSize";
import { PortfolioExportTableHeader } from "@/tools/aggregate/portfolio-export/components/PortfolioExportTableHeader";
import { ANALYSIS_PAGE_SIZE } from "@/tools/aggregate/portfolio-export/constants";
import { AutoAdaptationStatus, PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { Tool, ToolProps } from "@/types/tools";
import { useCanCreatePrivateLss, useCanCreatePrivatePortfolio } from "@/utils/hooks/useCanCreatePrivate";
import { useKeys } from "@/utils/hooks/useKeys";
import { UserGroupFilter } from "@/components/inputs/UserGroupFilter";
import { getBaseThemeName, getContactSupportLink, getHazardList } from "@/features/branding";
import { useCreatablePortfolioTypes } from "@/utils/hooks/useCreatablePortfolioTypes";
import IndicatorBadge from "@/components/status/IndicatorBadge";
import { decryptData, encryptData, encryptSecret, getSecretKey } from "@/features/cryptography";
import { Hazard } from "@/features/pe_aldd";
import { LargeSiteScreenTutorialBridge } from "@/tools/aggregate/portfolio-export/components/LargeSiteScreenTutorialBridge";
import PortfolioExportFairUseWarning from "@/tools/aggregate/portfolio-export/components/PortfolioExportFairUseWarning";
import { PortfolioExportJobTableBody, PortfolioListState } from "@/tools/aggregate/portfolio-export/components/PortfolioExportJobTableBody";
import { PortfolioExportPendingKeys } from "@/tools/aggregate/portfolio-export/components/PortfolioExportPendingKeys";
import { PortfolioInsightsTutorial } from "@/tools/aggregate/portfolio-export/components/PortfolioInsightsTutorial";
import { PortfolioPasswordInput } from "@/tools/aggregate/portfolio-export/components/PortfolioPasswordInput";
import { useAnalysisListFilters } from "@/tools/aggregate/portfolio-export/hooks/useAnalysisListFilters";
import { useKeysLocked } from "@/tools/aggregate/portfolio-export/hooks/useKeysLocked";
import { PortfolioTutorialProvider } from "@/tools/aggregate/portfolio-export/PortfolioTutorialContext";
import LargeSiteScreenFairUseWarning from "@/tools/risk-bands/large-site-screen/components/LargeSiteScreenFairUseWarning";
import { KeyPairAndSalt } from "@/types/cryptography";
import { ProcessStatus } from "@/types/processes";
import { arrayBufferToString, hexToArrayBuffer } from "@/utils/crypto";
import { useAdaptationPermissions } from "@/utils/hooks/useAdaptationPermissions";
import Loading3QuartersOutlined from "@ant-design/icons/es/icons/Loading3QuartersOutlined";
import ProjectOutlined from "@ant-design/icons/es/icons/ProjectOutlined";
import { useLocalStorage, useTimeout } from "@mantine/hooks";
import * as Sentry from "@sentry/nextjs";
import { useSession } from "@supabase/auth-helpers-react";
import { Alert, AlertProps, Button, Modal, notification, Select, Space, Spin } from "antd";
import { isEqual } from "lodash";
import Link from "next/link";
import { useRouter } from "next/router";
import { ComponentProps, FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
interface PortfolioExportToolProps extends ToolProps {
  mode?: "large-site" | "points";
  tableBodyComponent?: typeof PortfolioExportJobTableBody;
  tableHeaderComponent?: typeof PortfolioExportTableHeader;
}
interface PEAlertProps extends AlertProps {
  key: string;
}
export const PortfolioExportTool = ({
  mode = "points",
  tableBodyComponent = PortfolioExportJobTableBody,
  tableHeaderComponent = PortfolioExportTableHeader,
  ...props
}: PortfolioExportToolProps) => {
  const TableBodyComponent = tableBodyComponent;
  const TableHeaderComponent = tableHeaderComponent;
  const isLargeSite = mode === "large-site";
  const {
    t
  } = useTranslation("terms");
  const {
    canRunAutoAdaptation
  } = useAdaptationPermissions({
    isLargeSite
  });
  const [loadTimeoutFinished, setLoadTimeoutFinished] = useState(false);
  useTimeout(() => setLoadTimeoutFinished(true), 500, {
    autoInvoke: true
  });
  const [hideAutomatedResilience, setHideAutomatedResilience] = useLocalStorage<boolean>({
    key: "automatedResilience.hidePortfolioBanner",
    defaultValue: false
  });
  const {
    regionTypes: availablePortfolioTypes,
    isPrivate,
    isReady: areFiltersReady,
    keysReceivedState,
    setKeysReceivedState
  } = useAnalysisListFilters();
  const creatablePortfolioTypes = useCreatablePortfolioTypes();
  const ORG_KEY_POLL_MS = 10_000;
  const [currentPage, setCurrentPage] = useState(1);
  const [progressTracker, setProgressTracker] = useState<PortfolioExportProgressTracker>();
  const [error, setError] = useState<null | {
    message: string;
    description: string[];
  }>(null);
  const [jobSchemas, setJobSchemas] = useState<PortfolioExportJobSchema[]>([]);
  const [isDecrypting, setIsDecrypting] = useState(true);
  /**
   * The query result the rows on screen were decrypted from.
   *
   * The fetch settling and the decrypt starting are separate renders, so without
   * this the list reports itself settled while still showing the last filter's
   * rows. RTK's structural sharing keeps an unchanged refetch from reading stale.
   */
  const [decryptedFrom, setDecryptedFrom] = useState<unknown>(null);
  const [alerts, setAlerts] = useState<PEAlertProps[]>([]);
  const [hasJustReceivedKeys, setHasJustReceivedKeys] = useState(false);
  const [showPendingKeysModal, setShowPendingKeysModal] = useState(false);
  const [filteredUserId, setFilteredUserId] = useState("");
  const [filteredGroupId, setFilteredGroupId] = useState("");
  const [filteredRegionType, setFilteredRegionType] = useState("");
  const [queryParamsLoaded, setQueryParamsLoaded] = useState(false);
  const refProgressTracker = useRef<PortfolioExportProgressTracker>();
  const [notify, notificationContext] = notification.useNotification();
  const availablePortfolioTypesRef = useRef(availablePortfolioTypes);
  availablePortfolioTypesRef.current = availablePortfolioTypes;
  const creatablePortfolioTypesRef = useRef(creatablePortfolioTypes);
  creatablePortfolioTypesRef.current = creatablePortfolioTypes;
  const TableHeaderComponentRef = useRef(TableHeaderComponent);
  TableHeaderComponentRef.current = TableHeaderComponent;
  const TableBodyComponentRef = useRef(TableBodyComponent);
  TableBodyComponentRef.current = TableBodyComponent;
  const [TableHeader] = useState(() => (props: any) => <TableHeaderComponentRef.current {...props} availablePortfolioTypes={availablePortfolioTypesRef.current} creatablePortfolioTypes={creatablePortfolioTypesRef.current} />);
  const [TableBody] = useState(() => (props: ComponentProps<typeof TableBodyComponent>) => <TableBodyComponentRef.current {...props} availablePortfolioTypes={availablePortfolioTypesRef.current} />);
  const session = useSession();
  const router = useRouter();
  const [keyPair] = useKeys();
  const canCreatePrivatePortfolio = useCanCreatePrivatePortfolio();
  const canCreatePrivateLss = useCanCreatePrivateLss();
  const canCreatePrivate = isLargeSite ? canCreatePrivateLss : canCreatePrivatePortfolio;
  const [orgKeyPollInterval, setOrgKeyPollInterval] = useState(ORG_KEY_POLL_MS);
  const {
    data: currentUser
  } = useGetCurrentUserQuery(undefined, {
    pollingInterval: orgKeyPollInterval
  });
  const {
    data: distinctUsers
  } = useGetDistinctPortfolioUsersQuery({
    isLargeSite
  });
  const {
    data: toolUsage
  } = useGetToolUsageQuery({
    activeQuotaOnly: true
  });
  const {
    data: groups
  } = useGetGroupsQuery();
  const {
    data: users
  } = useGetUsersQuery();
  const regionTypes = filteredRegionType ? [filteredRegionType] : availablePortfolioTypes;
  const portfolios = useGetPortfoliosQuery({
    isLargeSite,
    page: currentPage,
    pageSize: ANALYSIS_PAGE_SIZE,
    userId: filteredUserId,
    groupId: filteredGroupId,
    regionTypes,
    isPrivate
  }, {
    skip: !queryParamsLoaded || !areFiltersReady
  });
  const keyPairNotPresent = useKeysLocked();
  const pendingRerunId = useSelector((state: StoreState) => state.portfolioExport.pendingRerunPortfolioId);
  const hasActiveFilters = !!(filteredUserId || filteredGroupId || filteredRegionType);
  const listState: PortfolioListState = (() => {
    // Stale means the query has newer data than what is currently decrypted on
    // screen - true between the fetch resolving and the decrypt finishing.
    const isStale = !!portfolios.data && portfolios.data !== decryptedFrom;
    const isBusy = portfolios.isUninitialized || portfolios.isFetching || isDecrypting || isStale;
    if (isBusy) return jobSchemas.length ? {
      kind: "refreshing",
      rows: jobSchemas
    } : {
      kind: "loading"
    };
    if (jobSchemas.length) return {
      kind: "ready",
      rows: jobSchemas
    };
    return {
      kind: "empty",
      reason: hasActiveFilters ? "no-results" : "no-data"
    };
  })();
  const clearFilters = () => {
    setFilteredUserId("");
    setFilteredGroupId("");
    setFilteredRegionType("");
    setCurrentPage(1);
  };

  /** This ensures that we don't have to wait until state update to get updated value. */
  refProgressTracker.current = progressTracker;
  useEffect(() => {
    if (!pendingRerunId) return;
    if (keyPairNotPresent) return;
    if (listState.kind !== "ready" && listState.kind !== "empty") return;

    // no action if no user confirmed re-run is pending
    if (!store.getState().portfolioExport.pendingRerunPortfolioId) return;

    // error notif if re-processed analysis id is not in page
    notify.error({
      message: "Couldn't start re-processing",
      description: "Find the analysis in the list and use Update available to try again."
    });
    // remove analysis to re-process if not found
    store.dispatch(portfolioExportActions.removeRerunningPortfolio(pendingRerunId));
    store.dispatch(portfolioExportActions.clearPendingRerun());
  }, [pendingRerunId, keyPairNotPresent, listState.kind]);
  useEffect(() => {
    const abandonPendingRerun = (_url: string, {
      shallow
    }: {
      shallow: boolean;
    }) => {
      // The filter/page sync below navigates shallowly (eg: portfolio locked password prompt); the user is still here.
      if (shallow) return;
      const heldRerunId = store.getState().portfolioExport.pendingRerunPortfolioId;
      if (!heldRerunId) return;

      // re-run discarded before starting if user navigates away
      store.dispatch(portfolioExportActions.removeRerunningPortfolio(heldRerunId));
      store.dispatch(portfolioExportActions.clearPendingRerun());
    };
    router.events.on("routeChangeStart", abandonPendingRerun);
    return () => router.events.off("routeChangeStart", abandonPendingRerun);
  }, [router.events]);

  /**
   * Stop polling for the organisation's encryption keys once they arrive, and
   * resume if they ever go away.
   */
  useEffect(() => {
    setOrgKeyPollInterval(currentUser?.has_org_keys ? 0 : ORG_KEY_POLL_MS);
  }, [currentUser?.has_org_keys]);
  useEffect(() => {
    const {
      userId,
      groupId,
      regionType,
      page
    } = router.query;
    if (typeof userId === "string") setFilteredUserId(userId);
    if (typeof groupId === "string") setFilteredGroupId(groupId);
    if (typeof regionType === "string") setFilteredRegionType(regionType);
    if (typeof page === "string") {
      if (page === "1") {
        setCurrentPage(1);
      } else {
        const parsedPage = parseInt(page, 10);
        if (!isNaN(parsedPage)) {
          setCurrentPage(parsedPage);
        }
      }
    }
    setQueryParamsLoaded(true);
  }, [router.query]);
  useEffect(() => {
    const query = {
      ...router.query
    };
    if (filteredUserId) {
      query.userId = filteredUserId;
    } else {
      delete query.userId;
    }
    if (filteredGroupId) {
      query.groupId = filteredGroupId;
    } else {
      delete query.groupId;
    }
    if (filteredRegionType) {
      query.regionType = filteredRegionType;
    } else {
      delete query.regionType;
    }
    if (currentPage && currentPage !== 1) {
      query.page = String(currentPage);
    } else {
      delete query.page;
    }
    router.replace({
      pathname: router.pathname,
      query
    }, undefined, {
      shallow: true
    });
  }, [filteredUserId, filteredGroupId, filteredRegionType, currentPage]);
  useEffect(() => {
    const orgSecretExists = !!users?.find(u => u.has_org_keys);
    if (users && !orgSecretExists) {
      Sentry.captureMessage(`orgSecretExists: No users with org secret found`, {
        tags: {
          section: "Encryption keys"
        },
        extra: {
          orgId: currentUser?.org_id,
          userId: currentUser?.id
        }
      });
      setAlerts([{
        key: "locked",
        type: "error",
        showIcon: true,
        message: "Encryption Keys Locked",
        description: "It looks like no one in your organisation currently holds the encryption keys required to access your secure files. As a result, these keys cannot be restored or shared with your account. Please contact XDI Support to reset your organisation’s encryption key. Important: Any files previously encrypted with your organisation's old keys will be lost."
      }]);
      return;
    }
    const newAlerts: PEAlertProps[] = [];

    // When user previously accessed page without key, but now has them, show notice.
    if (keyPair.user && currentUser?.has_org_keys && (keysReceivedState === "awaiting" || hasJustReceivedKeys)) {
      setHasJustReceivedKeys(true);
      setKeysReceivedState("received");
      newAlerts.push({
        key: "received",
        message: "Keys Received",
        description: <>
            Your organisation&apos;s encryption keys have been successfully received. You can now securely create and
            access shared files with other members of your group.
          </>,
        type: "success",
        showIcon: true,
        closable: !!keyPair.org,
        onClose: () => {
          setHasJustReceivedKeys(false);
        }
      });
    }
    if (keyPair.user && !currentUser?.has_org_keys) {
      // When the user accesses the page without org key, show an awaiting message.
      setKeysReceivedState("awaiting");
      newAlerts.push({
        key: "awaiting",
        message: <Space>
            <span>Awaiting Encryption Keys</span>
            <Spin className="mb-1" indicator={<Loading3QuartersOutlined spin />} size="small" />
          </Space>,
        description: <Space orientation="vertical">
            <span>
              You require your organisation&apos;s encryption keys to enable access to secure files. Please try again
              later or ask an authorised user from your organisation to log in and share the encryption keys.
              {canCreatePrivate && " In the meantime you can only create & access private files."}
            </span>
            {getBaseThemeName() !== "hkma" && <span>
                For more information about the XDI Hub&apos;s Encryption keys,{" "}
                <Link target="_blank" href="/faqs/what-are-encryption-keys-on-the-xdi-climate-risk-hub">
                  read this article
                </Link>
                .
              </span>}
            <Button onClick={() => setShowPendingKeysModal(true)}>View Authorised Users List</Button>
          </Space>,
        type: "info",
        showIcon: true
      });
    }
    setAlerts(newAlerts);
  }, [currentUser, keyPair, keysReceivedState, hasJustReceivedKeys, users, canCreatePrivate]);
  useEffect(() => {
    props.setIsTutorialDisabled(keyPairNotPresent);
  }, [keyPairNotPresent]);
  useEffect(() => {
    if (!keyPair.user) return;
    const tracker = new PortfolioExportProgressTracker(updateSchema, {
      user: keyPair.user,
      org: keyPair.org
    });
    setProgressTracker(tracker);
  }, [keyPair.user, keyPair.org]);
  useEffect(() => {
    if (!jobSchemas.length || !progressTracker) return;
    progressTracker.schemas = jobSchemas;
    progressTracker.progress(canRunAutoAdaptation).catch(err => {
      if (err.name === "AbortError") return;
      console.error("Error while tracking progress: ", err);
      setError(err);
    });
  }, [jobSchemas, progressTracker]);
  useEffect(() => {
    if (!keyPair.user || !portfolios?.data) {
      setIsDecrypting(false);
      return;
    }
    processPortfolios();
  }, [portfolios?.data, keyPair.user, keyPair.org]);

  /**
   * Update a specific schema.
   */
  const updateSchema = (updatedSchema: PortfolioExportJobSchema) => {
    setJobSchemas((prev: PortfolioExportJobSchema[]) => prev.map((schema: PortfolioExportJobSchema) => schema.id === updatedSchema.id && !isEqual(schema, updatedSchema) ? updatedSchema : schema));
  };
  const processPortfolios = async () => {
    const {
      user,
      org
    } = keyPair;
    const items = portfolios?.data?.items;
    setIsDecrypting(true);
    if (!items || !user) {
      setDecryptedFrom(portfolios?.data);
      setIsDecrypting(false);
      return;
    }
    try {
      const schemaPromises = items.map(item => transformToJobSchema(item, user, org));
      const results = await Promise.allSettled(schemaPromises);
      const validSchemas = results.filter(isSuccessfulSchema).map(result => result.value).filter(isValidSchema);
      setJobSchemas(validSchemas);
    } catch (err) {
      console.error("Error processing portfolios:", err);
      setError({
        message: "Failed to process portfolios",
        description: [String(err)]
      });
    } finally {
      setDecryptedFrom(portfolios?.data);
      setIsDecrypting(false);
    }
  };
  const transformToJobSchema = async (item: PortfolioView | LargeSiteView, user: KeyPairAndSalt, org?: KeyPairAndSalt | null): Promise<PortfolioExportJobSchema> => {
    const existingSchema = jobSchemas.find(schema => schema.id === item.id);
    const privateKey = item.is_private ? user.keyPair.privateKey : org?.keyPair.privateKey;
    if (!privateKey) {
      throw new Error(`Portfolio ${item.id} requires ${item.is_private ? "user" : "org"} private key`);
    }
    const secret = await getSecretKey(privateKey, item.secret_enc!);
    const name = item.name ? await decryptData(secret, hexToArrayBuffer(item.name)) : "Untitled Portfolio Export";
    if (existingSchema && isCompletedStatus(existingSchema.status)) {
      const hasChanges = existingSchema.groupName !== item.group_name || existingSchema.name !== name || existingSchema.isPrivate !== item.is_private || existingSchema.isSummaryGenerated !== item.is_summary_generated || existingSchema.summaryId !== item.summary_id || existingSchema.rerunAt !== (item.rerun_at ?? null) || existingSchema.autoAdaptationStatus !== (item.auto_adaptation_status ?? null);
      if (!hasChanges) {
        return existingSchema;
      }
    }

    // Decrypt portfolio data
    const metadata = item.metadata ? JSON.parse(await decryptData(secret, hexToArrayBuffer(item.metadata))) : undefined;
    return {
      id: item.id!,
      name,
      assets: {
        errorCount: item.error,
        totalCount: item.total,
        processedCount: item.success,
        unprocessedCount: item.unprocessed
      },
      startTime: new Date(item.created_at!).getTime(),
      status: getJobStatus(item, existingSchema),
      summaryId: item.summary_id || "",
      userId: item.user_id || "",
      email: item.email || "Unknown User",
      rerunEmail: item.rerun_email ?? null,
      rerunAt: item.rerun_at ?? null,
      groupId: item.group_id || "",
      groupName: item.group_name || "",
      secret,
      isLargeSite,
      isPrivate: item.is_private || false,
      isSummaryGenerated: item.is_summary_generated,
      stamps: item.stamps || [],
      metadata,
      incorporatedHazards: "incorporated_hazards" in item && item.incorporated_hazards ? item.incorporated_hazards as Hazard[] : getHazardList().fallback,
      regionType: "region_type" in item && item.region_type ? item.region_type as "hk" | "global" : undefined,
      autoAdaptationStatus: (item.auto_adaptation_status ?? null) as AutoAdaptationStatus,
      collectionVersion: "collection_version" in item ? item.collection_version ?? null : null,
      updateAvailable: item.update_available ?? false,
      adaptations: undefined
    };
  };
  const isSuccessfulSchema = (result: PromiseSettledResult<PortfolioExportJobSchema>): result is PromiseFulfilledResult<PortfolioExportJobSchema> => {
    // exclude errored portfolios; presumably could not be decrypted
    return result.status === "fulfilled";
  };
  const isValidSchema = (schema: PortfolioExportJobSchema): boolean => {
    // Only include completed portfolios for other users
    // to avoid multiple batch uploads & progress connection
    return schema.userId === session?.user.id || isCompletedStatus(schema.status);
  };
  const isCompletedStatus = (status: string): boolean => {
    return ["completed", "success", "completed_with_errors"].includes(status);
  };
  const getJobStatus = (item: PortfolioView | LargeSiteView, existingSchema?: PortfolioExportJobSchema): ProcessStatus => {
    if (item.pending === 0 && item.error) return "completed_with_errors";
    if (item.pending === 0) return "completed";
    if (item.total === item.success + item.error && item.total > 0) return "completed";
    if (existingSchema?.status) return existingSchema.status;
    if (item.unprocessed) {
      store.dispatch(portfolioExportActions.setNewRunTotalInBatchTo({
        id: item.id!,
        count: item.unprocessed
      }));
      return "uploading";
    }
    return "processing";
  };
  return <PortfolioTutorialProvider isTutorialOpen={props.isTutorialOpen} canRunAutoAdaptation={canRunAutoAdaptation} isLargeSite={isLargeSite} data-sentry-element="PortfolioTutorialProvider" data-sentry-component="PortfolioExportTool" data-sentry-source-file="index.tsx">
      {process.env.NEXT_PUBLIC_THEME_NAME?.startsWith("hkma") && <PortfolioExportMaxSize />}
      {notificationContext}

      <Modal closable onCancel={() => setShowPendingKeysModal(false)} width={540} open={showPendingKeysModal} footer={null} data-sentry-element="Modal" data-sentry-source-file="index.tsx">
        <PortfolioExportPendingKeys data-sentry-element="PortfolioExportPendingKeys" data-sentry-source-file="index.tsx" />
      </Modal>

      {alerts.map(({
      key,
      ...alert
    }) => <Alert key={key} className="mb-6 p-4" {...alert} />)}

      {error && <Alert className="mb-6 p-4" message={error.message || "An unexpected error has occurred"} description={error.description ? <ul>
                {error.description.map((err, index) => <li key={index}>{err}</li>)}
              </ul> : <>Please refresh your browser & try again. If the error persists, please {getContactSupportLink()}.</>} type="error" closable onClose={() => setError(null)} />}

      {canRunAutoAdaptation && !hideAutomatedResilience && loadTimeoutFinished && <Alert rootClassName="border-none mb-4 p-4" type="warning" closable={{
      closeIcon: true,
      onClose: () => setHideAutomatedResilience(true)
    }} description={<div className="text-yellow-800">
              <p className="relative mb-2 ml-10 text-base font-semibold">
                <IndicatorBadge mode="inline" placement="left" offset={[-6, -3]}>
                  {t("automatedResilience")} (BETA)
                </IndicatorBadge>
              </p>
              <p className="mb-2">
                {t("automatedResilience")} is a new capability within the XDI Hub that helps organisations rapidly
                identify, prioritise, and explore resilience opportunities by providing a fast, standardised assessment
                of how physical climate risk outcomes may change under different resilience assumptions.
              </p>
              <p className="mb-2">
                If {t("automatedResilience")} is enabled for your organisation, all new analyses conducted through the
                Portfolio Analysis tool will automatically include these additional insights. Eligible legacy analyses
                can also be reprocessed to generate {t("automatedResilience")} results.
              </p>
              <Link href={"/method/automated-resilience"}>
                <Button type="primary">Learn More</Button>
              </Link>
            </div>} />}
      {keyPair.user ? <>
          {currentUser?.has_org_keys && !keyPair.org ? <PortfolioPasswordInput prompt={hasJustReceivedKeys ? "You have been granted your organisation’s encryption keys. Verify your password to confirm the exchange." : undefined} isLoading={keyPair.isLoading} /> : <RemoteJobTable<PortfolioExportJobSchema> headerComponent={TableHeader as FC} headerProps={{
        actionBtns: <>
                    <div className="flex items-center justify-start gap-2">
                      <UserGroupFilter users={distinctUsers ?? []} groups={groups ?? []} selectedUserId={filteredUserId} selectedGroupId={filteredGroupId} onUserChange={id => {
              setCurrentPage(1);
              setFilteredUserId(id);
            }} onGroupChange={id => {
              setCurrentPage(1);
              setFilteredGroupId(id);
            }} />
                      {availablePortfolioTypes.length > 1 && <Select className="w-max" placeholder="All portfolio types" onChange={type => {
              setCurrentPage(1);
              setFilteredRegionType(type);
            }} value={filteredRegionType} popupMatchSelectWidth={false} defaultValue={""} options={[{
              value: "",
              label: "All portfolio types"
            }, ...availablePortfolioTypes.map(type => ({
              value: type,
              label: type === "hk" ? "HKSAR Portfolio" : "Global Portfolio"
            }))]} />}
                    </div>
                  </>
      }} jobSchemas={jobSchemas} onRunStart={console.log}>
              {toolUsage && (isLargeSite ? <LargeSiteScreenFairUseWarning data={toolUsage} /> : <PortfolioExportFairUseWarning data={toolUsage} />)}
              <TableBody canRunAutoAdaptation={canRunAutoAdaptation} totalSchemas={portfolios.data?.count || portfolios.data?.items.length || 0} currentPage={currentPage} onPageChange={(page: number) => {
          setCurrentPage(page);
        }} pageSize={ANALYSIS_PAGE_SIZE} state={listState} onClearFilters={clearFilters} updateSchema={updateSchema} onDelete={async (id: string) => {
          const action = supabaseApi.endpoints.deletePortfolio.initiate(id);
          const res = await store.dispatch(action);
          if ("error" in res && res.error) {
            console.error(res.error);
            notify.error({
              message: `Error deleting ${isLargeSite ? "large site" : "portfolio"}`,
              description: "message" in res.error ? res.error.message : "Something went wrong. Please try again."
            });
          }
        }} onEdit={async props => {
          if (!keyPair.user) return;
          let encryptedSecret = "";
          if (props.isPrivate) {
            encryptedSecret = arrayBufferToString(await encryptSecret({
              publicKey: keyPair.user.keyPair.publicKey,
              secret: props.schema.secret.secretAndSalt
            }));
          } else {
            if (keyPair.orgSecret && keyPair.org?.keyPair.publicKey) {
              encryptedSecret = arrayBufferToString(await encryptSecret({
                publicKey: keyPair.org.keyPair.publicKey,
                secret: props.schema.secret.secretAndSalt
              }));
            }
          }
          const details = {
            id: props.schema.id,
            name: await encryptData(props.schema.secret, props.name),
            groupId: props.groupId,
            isPrivate: props.isPrivate,
            secretEnc: encryptedSecret
          };
          const action = supabaseApi.endpoints.updatePortfolioDetails.initiate(details);
          const res = await store.dispatch(action);
          if ("error" in res && res.error) {
            console.error(res.error);
            notify.error({
              message: "Error moving portfolio",
              description: "message" in res.error ? res.error.message : "Something went wrong. Please try again."
            });
          } else {
            notify.success({
              message: "Portfolio updated!"
            });
          }
        }} onError={async (err, portfolioId) => {
          const action = supabaseApi.endpoints.deletePortfolio.initiate(portfolioId);
          await store.dispatch(action);
          if (typeof err === "object") {
            if ("status" in err) {
              switch (err.status) {
                case 403:
                  notify.error({
                    message: "An error occurred while contacting our results server. There could be an issue with your network configuration. Please contact XDI Support with the time/date of your analysis so we can troubleshoot.",
                    duration: false
                  });
                  return;
                case 422:
                  Sentry.captureException(`Analysis Error: onError: 422`, {
                    tags: {
                      section: "Analysis Error"
                    },
                    extra: {
                      userId: currentUser?.id,
                      orgId: currentUser?.org_id,
                      portfolioId,
                      error: err
                    }
                  });
                  notify.error({
                    message: "Uploaded data was in unexpected format. Please contact XDI Support with the time/date of your analysis so we can troubleshoot.",
                    duration: false
                  });
                  return;
              }
            }
            const errors = typeof err === "string" ? [err] : err;
            switch (errors[0]) {
              case "The uploaded file does not match the expected format.":
                notify.error({
                  message: "Uploaded data was in unexpected format. Please contact XDI Support with the time/date of your analysis so we can troubleshoot.",
                  duration: false
                });
                return;
            }
            notify.error({
              message: "An error occurred while processing your portfolio. Please try again or contact XDI Support.",
              duration: false
            });
            return;
          }
          setError({
            message: "Error while uploading your portfolio",
            description: Array.isArray(err) ? [...err] : [err]
          });
        }} />
            </RemoteJobTable>}
        </> : <PortfolioPasswordInput isLoading={keyPair.isLoading} />}

      {!isLargeSite && !keyPairNotPresent && <PortfolioInsightsTutorial onClose={() => props.setIsTutorialOpen(false)} open={props.isTutorialOpen} setState={
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {}} />}
      {isLargeSite && <LargeSiteScreenTutorialBridge />}
    </PortfolioTutorialProvider>;
};
export default {
  id: "portfolio-analysis",
  category: "comprehensive",
  keyPrefix: "comprehensive.portfolioInsights",
  icon: <ProjectOutlined />,
  render: (props: PortfolioExportToolProps) => <PortfolioExportTool {...props} />,
  hasTutorial: true,
  showUsage: true,
  maxWidth: "1440px"
} as Tool;